import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import Button, { RemoveButton } from '@/components/Button';
import { FormLayout, Input, Label, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useAlerts from '@/hooks/useAlerts';
import { ChannelGroupResource, EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';
import {
  EditorialFacebookPostNetwork,
  EditorialFacebookPostType,
} from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import { RootState } from '@/utils/store';

import ChannelsList from '../ChannelsList';
import FormPageGroupSwitch from '../FormPageGroupSwitch';
import PostFormOptionsField from '../PostFormOptionsField';

const NetworkInput = styled(Input)`
  text-transform: capitalize;
`;

const VariationWrap = styled(Larger)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: 24px;
  grid-column-gap: 24px;
  margin: 0 -32px;
  padding: 0 32px;
  padding: 24px 32px;
  background-color: ${({ theme }) => theme.colors.grayscaleSuperLight};
`;

const VariationHeading = styled(Larger)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NumberBadge = styled(Text)`
  display: flex;
  height: 24px;
  width: 48px;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.purpleLighter};
  border-radius: 100px;
`;

const AddButtonHolder = styled.div`
  text-align: right;
  margin-bottom: 8px;
  svg {
    margin-right: 8px;
  }
  button {
    width: 100%;
  }
`;

interface FormDataT {
  post_type: EditorialFacebookPostType;
  channel_id?: number;
  message: string;
  link: string;
  owner: string;
  image: string;
  site_visit_benchmark: string;
  reach_duration: number;
  option: string;
  channel_group_id: number | null;
  additional_channels_ids: { channel_id: number | null }[];
  pinterest_board_id?: number;
  use_shorter_url: boolean;
  is_fresh: number;
  notes: string;
}

interface Props {
  onClose: VoidFunction;
  activePage: EditorialQueuePage;
}

const StoryPostForm: FC<Props> = ({ onClose, activePage }) => {
  const [isChannelGroups, setIsChannelGroups] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const [network, networkLabel] = useNetwork();

  const userName = useSelector((state: RootState) => state.auth.user!.name);

  const {
    handleSubmit,
    clearErrors,
    control,
    watch,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      post_type: EditorialFacebookPostType.STORY,
      image: '',
      link: '',
      owner: userName,
      reach_duration: 60,
      site_visit_benchmark: '0',
      option: 'prepend',
      additional_channels_ids: [],
      channel_id: activePage.id,
      channel_group_id: null,
      use_shorter_url: false,
      is_fresh: 0,
      notes: '',
    },
  });

  const { data: channelGroups } = useQuery(
    ['channel_groups'],
    () =>
      api.get<never, ChannelGroupResource[]>(`/v1/distribution/channel-groups`),
    {
      staleTime: 1000 * 60,
    },
  );

  const { data: channels } = useQuery(
    ['editorial_distribution_queue_channels'],
    () => api.get<never, EditorialQueuePage[]>('/v1/editorial/queue/channels'),
    {
      staleTime: 120_000,
    },
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'additional_channels_ids',
  });

  const filteredChannels = useMemo(() => {
    if (network === EditorialFacebookPostNetwork.FACEBOOK) {
      return channels?.map((channel) => ({
        label: channel.name,
        value: channel.id,
      }));
    }

    return channels
      ?.filter((channel) => {
        switch (network) {
          case EditorialFacebookPostNetwork.INSTAGRAM:
            return !!channel.instagramAccountId;
          case EditorialFacebookPostNetwork.THREADS:
            return !!channel.threadsAccountId;
          case EditorialFacebookPostNetwork.PINTEREST:
            return !!channel.pinterestAccountId;
          default:
            return false;
        }
      })
      ?.map((channel) => ({ label: channel.name, value: channel.id }));
  }, [network, channels]);

  const channelGroup = watch('channel_group_id');

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(
      async ({
        channel_group_id,
        additional_channels_ids,
        channel_id,
        ...data
      }) => {
        try {
          await api.post(`/v1/editorial/queue/prepend`, {
            ...data,
            ...(!isChannelGroups && {
              additional_channels_ids,
              channel_id,
            }),
            ...(isChannelGroups && {
              channel_group_id,
            }),
            network,
          });

          await queryClient.invalidateQueries([
            'editorial_distribution_schedules',
          ]);

          onClose();

          alerts.success('A new post has been added successfully');
        } catch (err) {
          handleFormErrors<FormDataT>(err, setError, alerts, true);
        }
      },
    )(e);
  };

  return (
    <Modal>
      <SidePanel heading={`Add new post · Story`} onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <Label>Post Type</Label>
                <Input readOnly disabled value="Story" />
              </Larger>
              <Larger>
                <Label>Network</Label>
                <NetworkInput readOnly disabled value={networkLabel} />
              </Larger>
              <PostFormOptionsField control={control} />
              <FormField<FormDataT>
                id="imageField"
                label="Image"
                control={control}
                name="image"
                type="image"
                notes={'JPG or PNG'}
                accept="image/png, image/jpeg"
              />
              <FormField<FormDataT>
                label="Article Link"
                control={control}
                name="link"
                inputType="url"
              />
              <FormField<FormDataT>
                headline="Should use shortened Article Link"
                control={control}
                name="use_shorter_url"
                type="switch"
              />
              <FormField<FormDataT, number>
                label="Link Type"
                control={control}
                options={[
                  { label: 'Reuse', value: 0 },
                  { label: 'Fresh', value: 1 },
                ]}
                name="is_fresh"
                type="select"
              />
              <FormField<FormDataT>
                label="Owner"
                control={control}
                name="owner"
              />
              {network === EditorialFacebookPostNetwork.FACEBOOK && (
                <Larger>
                  <FormPageGroupSwitch
                    isChannelGroups={isChannelGroups}
                    setIsChannelGroups={setIsChannelGroups}
                  />
                </Larger>
              )}
              {!!isChannelGroups &&
              network === EditorialFacebookPostNetwork.FACEBOOK ? (
                <>
                  <FormField<FormDataT, number>
                    control={control}
                    type="select"
                    name="channel_group_id"
                    label="Page Group"
                    options={
                      channelGroups?.map((group) => {
                        return {
                          label: group.name,
                          value: group.id,
                        };
                      }) ?? []
                    }
                  />
                  {!!channelGroup && !!channelGroups?.length && (
                    <Larger>
                      <ChannelsList
                        currentGroup={channelGroups.find(
                          (group) => group.id === channelGroup,
                        )}
                      />
                    </Larger>
                  )}
                </>
              ) : (
                <>
                  <FormField<FormDataT, number>
                    key="page_name"
                    label="Page Name"
                    type="select"
                    control={control}
                    name="channel_id"
                    options={filteredChannels || []}
                  />
                  {fields.map((item, index) => (
                    <VariationWrap key={item.id}>
                      <VariationHeading>
                        <NumberBadge $type="smallBold" $color="white">
                          Nr. {index + 2}
                        </NumberBadge>
                        <RemoveButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(index);
                          }}
                        >
                          Remove
                        </RemoveButton>
                      </VariationHeading>
                      <FormField<FormDataT, number>
                        label="Page Name"
                        type="select"
                        control={control}
                        name={`additional_channels_ids.${index}.channel_id`}
                        options={filteredChannels || []}
                      />
                    </VariationWrap>
                  ))}
                  <Larger>
                    <AddButtonHolder>
                      <Button
                        type="button"
                        $type="colorized"
                        onClick={() => append({ channel_id: null })}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 2V14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M2 8H14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Add additional page
                      </Button>
                    </AddButtonHolder>
                  </Larger>
                </>
              )}
              <FormField<FormDataT>
                label="Site Visit Benchmark"
                control={control}
                placeholder="Add site visit benchmark"
                name={`site_visit_benchmark`}
              />
              <FormField<FormDataT>
                label="Reach Duration (min)"
                control={control}
                placeholder="Add reach duration"
                disabled
                name={`reach_duration`}
              />
              <FormField<FormDataT>
                label="Notes"
                control={control}
                type="textarea"
                placeholder="Add notes"
                name="notes"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Add
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default StoryPostForm;
